import React, { useEffect, useRef } from 'react';

// This is a specialized hook that helps with integrating Google Translate
// while preventing React DOM reconciliation errors and disabling hover effects
export const useGoogleTranslate = () => {
  // Maintain refs to track original functions and observers
  const refs = useRef({
    originalConsoleError: null,
    originalCreateElement: null,
    originalInsertBefore: null,
    originalRemoveChild: null,
    style: null,
    documentObserver: null,
    hoverObserver: null,
    hoverStyle: null,
    hoverEventListeners: null,
    elementsProcessed: new WeakSet(), // Track elements we've already processed
    isTranslating: false,
  });

  useEffect(() => {
    // Save original functions to refs if not already saved
    if (!refs.current.originalConsoleError) {
      refs.current.originalConsoleError = console.error;
    }
    
    if (!refs.current.originalCreateElement) {
      refs.current.originalCreateElement = document.createElement;
    }
    
    if (!refs.current.originalInsertBefore) {
      refs.current.originalInsertBefore = Node.prototype.insertBefore;
    }

    if (!refs.current.originalRemoveChild) {
      refs.current.originalRemoveChild = Node.prototype.removeChild;
    }

    // Create a more thorough error handling system
    console.error = (...args) => {
      const errorMsg = typeof args[0] === 'string' ? args[0] : String(args[0]);
      
      // More comprehensive list of error patterns to suppress
      const suppressPatterns = [
        'translate', 
        'm=el_', 
        'Cannot read properties of undefined',
        'Uncaught TypeError',
        'Uncaught Error',
        'googleTranslateElementInit',
        'Identifier',
        'has already been declared',
        'removeChild',
        'NotFoundError',
        'Maximum call stack size exceeded',
        'Node was not found'
      ];
      
      // Check if any pattern matches the error message
      if (suppressPatterns.some(pattern => errorMsg.includes(pattern))) {
        // Suppress Google Translate related errors
        return;
      }
      
      // Let other errors through
      refs.current.originalConsoleError.apply(console, args);
    };

    // Safe reference to original functions to avoid recursion
    const originalCreateElementSafe = refs.current.originalCreateElement;
    const originalRemoveChildSafe = refs.current.originalRemoveChild;

    // Patch removeChild to handle Google Translate's DOM manipulation safely
    Node.prototype.removeChild = function(child) {
      // Check if the child is actually a child of this node
      if (!this.contains(child)) {
        // Just return the child without throwing if it's not a child
        // This prevents the NotFoundError that breaks form submission
        return child;
      }
      
      try {
        return originalRemoveChildSafe.call(this, child);
      } catch (e) {
        // Gracefully handle any removeChild errors
        console.error('Safely handled removeChild error:', e);
        return child;
      }
    };

    // Block the creation of specific Google elements by modifying document.createElement
    document.createElement = function(tagName) {
      // CRITICAL: Use the original reference directly to avoid recursion
      const element = originalCreateElementSafe.apply(document, arguments);
      
      // Only process each element once to prevent recursion
      if (!refs.current.elementsProcessed.has(element)) {
        refs.current.elementsProcessed.add(element);
        
        // Add a mutation observer to catch when classes are added
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const className = element.className;
              if (typeof className === 'string' && 
                  (className.includes('VIpgJd-ZVi9od') || 
                   className.includes('goog-te'))) {
                element.style.display = 'none';
                // Prevent further modifications to this element
                observer.disconnect();
              }
            }
          });
        });
        
        // Start observing
        observer.observe(element, { attributes: true });
      }
      
      return element;
    };

    // Add CSS to hide Google's elements
    const style = originalCreateElementSafe.call(document, 'style');
    style.textContent = `
      /* Hide all Google Translate elements */
      .goog-te-banner-frame,
      .skiptranslate,
      .goog-te-gadget,
      [class*="VIpgJd-ZVi9od"],
      .VIpgJd-ZVi9od-aZ2wEe-wOHMyf,
      .VIpgJd-ZVi9od-aZ2wEe-OiiCO,
      .VIpgJd-ZVi9od-aZ2wEe,
      .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q-QFlW2 {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        height: 0 !important;
        width: 0 !important;
        position: absolute !important;
        top: -9999px !important;
        left: -9999px !important;
      }
      
      /* Disable hover styles but keep content visible */
      [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"],
      [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"] *,
      .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q,
      .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q * {
        background-color: transparent !important;
        box-shadow: none !important;
        border: none !important;
        cursor: inherit !important;
        pointer-events: none !important;
      }
      
      /* Ensure text is still visible and doesn't disappear */
      [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"],
      .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q {
        opacity: 1 !important;
        visibility: visible !important;
        display: inline !important;
        position: static !important;
        transition: none !important;
      }
      
      body {
        top: 0 !important;
      }
    `;
    document.head.appendChild(style);
    refs.current.style = style;

    // Override the insertBefore method to catch Google's elements
    Node.prototype.insertBefore = function(newNode, referenceNode) {
      // Check if this is a Google Translate related element
      if (newNode && newNode.nodeType === 1) {
        const className = newNode.className || '';
        if (typeof className === 'string') {
          // Hide Google's UI elements
          if (className.includes('VIpgJd-ZVi9od') || className.includes('goog-te')) {
            newNode.style.display = 'none';
            newNode.style.visibility = 'hidden';
          }
          
          // Disable hover effects but keep translated text visible
          if (className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS')) {
            // Disable hover styling without hiding the content
            newNode.style.backgroundColor = 'transparent';
            newNode.style.boxShadow = 'none';
            newNode.style.cursor = 'inherit';
            newNode.style.pointerEvents = 'none';
            
            // Clone to remove event listeners
            if (newNode.parentNode) {
              const clone = newNode.cloneNode(true);
              newNode.parentNode.replaceChild(clone, newNode);
              newNode = clone;
            }
          }
        }
      }
      
      // IMPORTANT: Make sure referenceNode is actually a child
      if (referenceNode && !this.contains(referenceNode)) {
        referenceNode = null; // Append at the end if reference node is invalid
      }
      
      try {
        return refs.current.originalInsertBefore.call(this, newNode, referenceNode);
      } catch (e) {
        // Handle insertBefore errors gracefully
        console.error('Safely handled insertBefore error:', e);
        return newNode;
      }
    };

    // Create a wrapper for the translation initialization
    if (!window.googleTranslateElementInitialized) {
      window.googleTranslateElementInitialized = true;
      
      // Define the initialization function
      window.googleTranslateElementInit = () => {
        try {
          if (window.google && window.google.translate && window.google.translate.TranslateElement) {
            new window.google.translate.TranslateElement(
              {
                pageLanguage: 'en',
                includedLanguages: 'en,es',
                layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
                autoDisplay: false,
              },
              'google_translate_element'
            );
            
            // After initialization, set up hover effect disabling
            disableTranslateHover();
          }
        } catch (error) {
          // Silently handle initialization errors
        }
      };
    }
    
    // Function to specifically disable Google Translate hover effects
    const disableTranslateHover = () => {
      // Create a MutationObserver to detect when Google adds its hover elements
      const hoverObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              // Check if this is a span with the VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q class
              if (node.nodeType === 1 && 
                  node.className && typeof node.className === 'string' && 
                  (node.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q') || 
                   node.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS'))) {
                
                // Method 1: Remove event listeners by cloning
                const clone = node.cloneNode(true);
                if (node.parentNode) {
                  node.parentNode.replaceChild(clone, node);
                }
                
                // Method 2: Override the onmouseover and onmouseout properties
                clone.onmouseover = null;
                clone.onmouseout = null;
                clone.onmousemove = null;
                
                // Method 3: Add inline styles to prevent hover effects
                clone.style.backgroundColor = 'transparent';
                clone.style.boxShadow = 'none';
                clone.style.cursor = 'inherit';
                clone.style.pointerEvents = 'none';
                
                // Method 4: Modify the class to remove hover functionality
                if (clone.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q')) {
                  clone.className = clone.className.replace('VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q', 'gt-no-hover');
                }
                
                // Make sure all child elements remain visible
                Array.from(clone.children).forEach(child => {
                  child.style.display = 'inline';
                  child.style.visibility = 'visible';
                  child.style.opacity = '1';
                });
              }
              
              // Target the popup element that appears on hover
              if (node.nodeType === 1 && 
                  node.className && typeof node.className === 'string' && 
                  node.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q-QFlW2')) {
                // Remove the popup element completely
                if (node.parentNode) {
                  node.parentNode.removeChild(node);
                }
              }
            });
          }
        });
      });
      
      // Start observing the entire document
      hoverObserver.observe(document.body, { 
        childList: true, 
        subtree: true,
        attributes: true,
        attributeFilter: ['class'] 
      });
      
      // Store the observer in refs for cleanup
      refs.current.hoverObserver = hoverObserver;
      
      // Also create a style element with more aggressive CSS
      const hoverStyle = originalCreateElementSafe.call(document, 'style');
      hoverStyle.textContent = `
        /* Target all possible hover elements more aggressively */
        [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"],
        [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"] *,
        .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q,
        .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q * {
          background-color: transparent !important;
          box-shadow: none !important;
          cursor: inherit !important;
          pointer-events: none !important;
          border: none !important;
          outline: none !important;
        }
        
        /* Ensure text content is always visible */
        [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"],
        .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q {
          display: inline !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: static !important;
        }
        
        /* Hide popup but keep text visible */
        .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q-QFlW2,
        [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q-QFlW2"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        
        /* Disable hover transitions */
        [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"],
        [class*="VIpgJd-yAWNEb-VIpgJd-fmcmS"] *,
        .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q,
        .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q * {
          transition: none !important;
          transform: none !important;
        }
      `;
      document.head.appendChild(hoverStyle);
      refs.current.hoverStyle = hoverStyle;
      
      // Add a deep intercept for mouseover events
      const interceptHoverEvents = (event) => {
        if (event.target && event.target.className && 
            typeof event.target.className === 'string' && 
            (event.target.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS') || 
             event.target.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q'))) {
          // Prevent the default hover behavior
          event.stopPropagation();
          event.preventDefault();
          return false;
        }
      };
      
      // Capture events at the document level
      document.addEventListener('mouseover', interceptHoverEvents, true);
      document.addEventListener('mouseenter', interceptHoverEvents, true);
      document.addEventListener('mousemove', interceptHoverEvents, true);
      
      // Store event listeners for cleanup
      refs.current.hoverEventListeners = {
        mouseover: interceptHoverEvents,
        mouseenter: interceptHoverEvents,
        mousemove: interceptHoverEvents
      };
    };

    // Load the Google Translate script with a retry mechanism
    let retryCount = 0;
    const maxRetries = 3;
    
    function loadScript() {
      // Check if script is already loaded
      const existingScript = document.querySelector('script[src*="translate_a/element.js"]');
      if (existingScript) {
        return;
      }
      
      // CRITICAL: Use original reference to avoid recursion
      const script = originalCreateElementSafe.call(document, 'script');
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      script.onerror = () => {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(loadScript, 1000); // Retry after 1 second
        }
      };
      document.body.appendChild(script);
    }
    
    // Create a hidden element for Google Translate if it doesn't exist
    if (!document.getElementById('google_translate_element')) {
      // CRITICAL: Use original reference to avoid recursion
      const div = originalCreateElementSafe.call(document, 'div');
      div.id = 'google_translate_element';
      div.style.display = 'none';
      document.body.appendChild(div);
    }
    
    // Delay loading the script to ensure our DOM modifications are in place
    setTimeout(loadScript, 100);
    
    // Set up a document-level mutation observer for any elements that might slip through
    const documentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              if (node.className && typeof node.className === 'string') {
                // Hide Google UI elements
                if (node.className.includes('VIpgJd-ZVi9od') || 
                    node.className.includes('goog-te')) {
                  node.style.display = 'none';
                  node.style.visibility = 'hidden';
                }
                
                // Disable hover effects on translated text
                if (node.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS') ||
                    node.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q')) {
                  // Remove hover styling without hiding content
                  node.style.backgroundColor = 'transparent';
                  node.style.boxShadow = 'none';
                  node.style.cursor = 'inherit';
                  node.style.pointerEvents = 'none';
                  
                  // Apply the same to all children
                  if (node.children && node.children.length) {
                    Array.from(node.children).forEach(child => {
                      child.style.display = 'inline';
                      child.style.visibility = 'visible';
                      child.style.opacity = '1';
                    });
                  }
                  
                  // Remove popup element that appears on hover
                  if (node.className.includes('VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q-QFlW2')) {
                    if (node.parentNode) {
                      node.parentNode.removeChild(node);
                    }
                  }
                }
              }
            }
          });
        }
      });
    });
    
    // Start observing the document with the configured parameters
    documentObserver.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['class'] 
    });
    refs.current.documentObserver = documentObserver;
    
    // Safe cleanup function that handles potential DOM conflicts
    return () => {
      // Only restore original functions if they're still our overridden versions
      if (console.error !== refs.current.originalConsoleError) {
        console.error = refs.current.originalConsoleError;
      }
      
      if (document.createElement !== originalCreateElementSafe) {
        document.createElement = refs.current.originalCreateElement;
      }
      
      if (Node.prototype.insertBefore !== refs.current.originalInsertBefore) {
        Node.prototype.insertBefore = refs.current.originalInsertBefore;
      }
      
      if (Node.prototype.removeChild !== originalRemoveChildSafe) {
        Node.prototype.removeChild = refs.current.originalRemoveChild;
      }
      
      // Safe style removal
      if (refs.current.style && refs.current.style.parentNode) {
        try {
          refs.current.style.parentNode.removeChild(refs.current.style);
        } catch (e) {
          // Suppress removal errors
        }
      }
      
      // Safe hover style removal
      if (refs.current.hoverStyle && refs.current.hoverStyle.parentNode) {
        try {
          refs.current.hoverStyle.parentNode.removeChild(refs.current.hoverStyle);
        } catch (e) {
          // Suppress removal errors
        }
      }
      
      // Safe observer disconnect
      if (refs.current.documentObserver) {
        try {
          refs.current.documentObserver.disconnect();
        } catch (e) {
          // Suppress disconnect errors
        }
      }
      
      // Safe hover observer disconnect
      if (refs.current.hoverObserver) {
        try {
          refs.current.hoverObserver.disconnect();
        } catch (e) {
          // Suppress disconnect errors
        }
      }
      
      // Remove event listeners
      if (refs.current.hoverEventListeners) {
        for (const [event, listener] of Object.entries(refs.current.hoverEventListeners)) {
          document.removeEventListener(event, listener, true);
        }
      }
      
      // Clear element tracking
      refs.current.elementsProcessed = new WeakSet();
    };
  }, []);

  // Function to safely submit a form when in non-English language
  const safeSubmit = (formElement, event) => {
    if (!formElement) return false;
    
    const currentLang = getCurrentLanguage();
    if (currentLang !== 'en') {
      // We're in a translated state, need to handle submission carefully
      if (event) {
        event.preventDefault();
      }
      
      // Temporarily disable our patches to let the form submit normally
      const tempFormSubmit = () => {
        try {
          // Create a cloned form that's not affected by Google Translate
          const tempForm = formElement.cloneNode(true);
          tempForm.style.display = 'none';
          document.body.appendChild(tempForm);
          
          // Copy form data
          Array.from(formElement.elements).forEach(element => {
            const name = element.name;
            if (name) {
              const tempElement = tempForm.elements[name];
              if (tempElement && element.value) {
                tempElement.value = element.value;
              }
            }
          });
          
          // Submit the cloned form
          tempForm.submit();
          
          // Clean up
          setTimeout(() => {
            if (document.body.contains(tempForm)) {
              document.body.removeChild(tempForm);
            }
          }, 1000);
          
          return true;
        } catch (e) {
          console.error("Safe form submission failed:", e);
          // Fall back to original form submission
          try {
            formElement.submit();
            return true;
          } catch (submitError) {
            console.error("Fallback form submission failed:", submitError);
            return false;
          }
        }
      };
      
      // Delay slightly to let any Google Translate operations finish
      setTimeout(tempFormSubmit, 50);
      return true;
    }
    
    // For English, allow normal submission
    return false;
  };
  const getCurrentLanguage = () => {
    const href = window.location.href;
    const pathname = window.location.pathname;
    const basename = process.env.REACT_APP_BASENAME || '';
    let pathLang = '';

  
  
    // 1. HIGHEST PRIORITY: Check URL ending for language (/en or /es)
    if (href.endsWith('/en') || href.endsWith('/en/') || pathname.endsWith('/en')) {
      return 'en';
    }
    if (href.endsWith('/es') || href.endsWith('/es/') || pathname.endsWith('/es')) {
      return 'es';
    }
    const envLang = process.env.REACT_APP_LANG;
    if (['en', 'es'].includes(envLang)) {
      return envLang;
    }
    // 2. SECOND PRIORITY: Check URL path structure
    if (basename && pathname.startsWith(basename)) {
      const pathWithoutBase = pathname.substring(basename.length);
      pathLang = pathWithoutBase.split('/')[1];
    } else {
      pathLang = pathname.split('/')[1];
    }
  
    if (['en', 'es'].includes(pathLang)) {
      return pathLang;
    }
  
    // 3. THIRD PRIORITY: Check Google Translate cookie
    const savedLang = document.cookie.replace(/(?:(?:^|.*;\s*)googtrans\s*=\s*([^;]*).*$)|^.*$/, '$1');
    if (savedLang) {
      const cookieLang = savedLang.includes('/es/') ? 'es' : 'en';
      return cookieLang;
    }
    return 'en';
  };
  
// Alternative: If you want to completely replace URL-based detection
const getCurrentLanguageEnvOnly = () => {
  // 1. HIGHEST PRIORITY: Environment variable
  const envLang = process.env.REACT_APP_LANG;
  if (['en', 'es'].includes(envLang)) {
    return envLang;
  }

  // 2. SECOND PRIORITY: Google Translate cookie
  const savedLang = document.cookie.replace(/(?:(?:^|.*;\s*)googtrans\s*=\s*([^;]*).*$)|^.*$/, '$1');
  if (savedLang) {
    return savedLang.includes('/es/') ? 'es' : 'en';
  }
  
  // 3. FALLBACK: Default to English
  return 'en';
};
 
  
  // Modified translateTo function to work with React Router
  const translateTo = (lang) => {
    
    // Set cookies for translation
    setTranslationCookies(lang);
    
    // We don't need to change URL here - React Router will handle that part
    // Just set up the translation cookies and state
    
    // Flag that we're actively translating to prevent multiple transitions
    if (refs.current.isTranslating !== true) {
      refs.current.isTranslating = true;
      
      // Set a timeout to reset the flag
      setTimeout(() => {
        refs.current.isTranslating = false;
      }, 500);
    }
  };

  // Helper function to set translation cookies
  const setTranslationCookies = (lang) => {
    if (lang === 'en') {
      // Clear translation cookies for English
      clearTranslationCookies();
      return;
    }
    
    // Set cookie for current domain
    document.cookie = `googtrans=/en/${lang}; path=/;`;
    
    // Set for specific domains
    const hostname = window.location.hostname;
    
    // Handle domain with and without www
    if (hostname !== 'localhost') {
      document.cookie = `googtrans=/en/${lang}; path=/; domain=${hostname};`;
      
      if (hostname.startsWith('www.')) {
        const domain = hostname.substring(4);
        document.cookie = `googtrans=/en/${lang}; path=/; domain=${domain};`;
      } else {
        document.cookie = `googtrans=/en/${lang}; path=/; domain=www.${hostname};`;
      }
      
      // Set for top-level domain
      const parts = hostname.split('.');
      if (parts.length > 1) {
        const topDomain = parts.slice(-2).join('.');
        document.cookie = `googtrans=/en/${lang}; path=/; domain=.${topDomain};`;
      }
    }
  };

  // Helper function to clear translation cookies
  const clearTranslationCookies = () => {
    const domains = [];
    const hostname = window.location.hostname;
    
    // Add current hostname
    domains.push('');  // No domain
    domains.push(hostname);
    
    // Add with/without www version
    if (hostname !== 'localhost') {
      if (hostname.startsWith('www.')) {
        domains.push(hostname.substring(4));
      } else {
        domains.push(`www.${hostname}`);
      }
      
      // Add top-level domain
      const parts = hostname.split('.');
      if (parts.length > 1) {
        domains.push(`.${parts.slice(-2).join('.')}`);
      }
    }
    
    // Clear cookies for all domains
    for (const domain of domains) {
      const cookieStr = domain
        ? `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain};`
        : `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      document.cookie = cookieStr;
    }
  };

  // Expose the needed functions
  return { translateTo, getCurrentLanguage, safeSubmit };
};

// Error boundary component to catch React errors with Google Translate
export class TranslateErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  
  componentDidCatch(error, info) {
  }
  
  render() {
    // Continue rendering children even when there are errors
    return this.props.children;
  }
}

// This component just adds the hidden Google Translate element to the DOM
const GoogleTranslateElement = () => {
  // Component doesn't create the element anymore - it's handled in the hook
  return null;
};

// Safe Form component
export const SafeTranslateForm = ({ onSubmit, children, ...props }) => {
  const formRef = useRef(null);
  const { getCurrentLanguage, safeSubmit } = useGoogleTranslate();
  
  const handleSubmit = (event) => {
    const isNonEnglish = getCurrentLanguage() !== 'en';
    
    // If we're in a non-English language, use our safe submit
    if (isNonEnglish) {
      const handled = safeSubmit(formRef.current, event);
      
      // If we handled the submission, we're done
      if (handled) {
        return;
      }
    }
    
    // Otherwise, call the provided onSubmit handler
    if (onSubmit) {
      onSubmit(event);
    }
  };
  
  return (
    <TranslateErrorBoundary>
      <form ref={formRef} onSubmit={handleSubmit} {...props}>
        {children}
      </form>
    </TranslateErrorBoundary>
  );
};

export default GoogleTranslateElement;